/* Collection links navigate; adjacent controls expand subcollections. */
(() => {
  if (window.eosAuditNavigationLoaded) return;
  window.eosAuditNavigationLoaded = true;
  const sorted = value => {
    const url = new URL(value,location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/collections/') || url.pathname.includes('/products/')) return null;
    if (!url.searchParams.get('sort_by')) url.searchParams.set('sort_by','best-selling');
    return url;
  };
  if (!customElements.get('eos-nav-branch')) customElements.define('eos-nav-branch',class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const button = this.querySelector('[data-eos-nav-toggle]'), children = this.querySelector('[data-eos-nav-children]');
      if (!button || !children) return;
      this.ready = true;
      const label = button.getAttribute('aria-label') || 'Show subcollections';
      const setOpen = value => {
        children.hidden = !value;
        button.setAttribute('aria-expanded',String(value));
        button.setAttribute('aria-label',value ? label.replace('Show ','Hide ') : label);
      };
      this.setOpen = setOpen;
      button.hidden = false; setOpen(false);
      button.addEventListener('click',() => setOpen(children.hidden));
      this.addEventListener('keydown',event => {
        if (event.key === 'Escape' && !children.hidden) {
          event.preventDefault(); event.stopPropagation();
          this.handledEscape = true; setOpen(false); button.focus();
        }
      });
      this.addEventListener('keyup',event => {
        if (event.key === 'Escape' && this.handledEscape) {
          event.stopPropagation(); this.handledEscape = false;
        }
      });
    }
  });
  const measuredDrawers = new Map();
  const outerHeight = el => {
    if (!el) return 0;
    const style = getComputedStyle(el);
    return el.getBoundingClientRect().height + (parseFloat(style.marginTop)||0) + (parseFloat(style.marginBottom)||0);
  };
  function fitDrawer(drawer) {
    if (measuredDrawers.has(drawer)) { measuredDrawers.get(drawer).schedule(); return; }
    const toggle = drawer.querySelector(':scope > details'), panel = drawer.querySelector('#menu-drawer');
    if (!toggle || !panel) return;
    const controller = new AbortController();
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!drawer.isConnected || !toggle.open || !panel.getClientRects().length) return;
      const active = [...panel.querySelectorAll('details[open] > .menu-drawer__submenu')].filter(el => el.parentElement.classList.contains('menu-opening')).pop();
      let needed;
      if (active) {
        const inner = active.querySelector(':scope > .menu-drawer__inner-submenu') || active;
        const back = inner.querySelector(':scope > .menu-drawer__close-button');
        const list = inner.querySelector(':scope > .menu-drawer__menu');
        needed = outerHeight(back) + outerHeight(list) + 18;
      } else {
        const nav = panel.querySelector('.menu-drawer__navigation');
        const list = nav?.querySelector(':scope > ul');
        const style = nav ? getComputedStyle(nav) : null;
        needed = outerHeight(list) + outerHeight(panel.querySelector('.menu-drawer__utility-links')) + (parseFloat(style?.paddingTop)||0) + (parseFloat(style?.paddingBottom)||0) + 2;
      }
      const viewport = window.visualViewport?.height || window.innerHeight;
      const available = Math.max(120,viewport - panel.getBoundingClientRect().top - 12);
      if (Number.isFinite(needed) && needed > 0) panel.style.setProperty('--eos-menu-height',Math.ceil(Math.min(needed,available)) + 'px');
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const observer = new MutationObserver(schedule);
    observer.observe(toggle,{attributes:true,childList:true,subtree:true,attributeFilter:['open','hidden','aria-expanded','class']});
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    panel.querySelectorAll('.menu-drawer__menu,.menu-drawer__utility-links').forEach(el => resize?.observe(el));
    window.addEventListener('resize',schedule,{signal:controller.signal});
    window.visualViewport?.addEventListener('resize',schedule,{signal:controller.signal});
    document.fonts?.ready.then(schedule);
    drawer.dataset.eosCompactMenu = 'true';
    measuredDrawers.set(drawer,{schedule,dispose:() => {controller.abort();observer.disconnect();resize?.disconnect();cancelAnimationFrame(frame);}});
    schedule();
  }
  function setupMobile() {
    for (const [drawer,state] of measuredDrawers) if (!drawer.isConnected) { state.dispose(); measuredDrawers.delete(drawer); }
    document.querySelectorAll('[data-eos-mobile-map]').forEach(source => {
      let entries; try { entries = JSON.parse(source.textContent); } catch (_) { return; }
      if (!Array.isArray(entries)) return;
      entries.forEach(entry => {
        const el = document.getElementById(entry.id); if (!el) return;
        let url; try { url = sorted(entry.url); } catch (_) { return; } if (!url) return;
        if (el.tagName === 'A') {
          el.href = url.pathname + url.search;
          const isChild = !!el.closest('[data-eos-mobile-children]');
          el.classList.toggle('eos-mobile-parent',!isChild);
          el.classList.toggle('eos-mobile-child',isChild);
          if (isChild) el.textContent = entry.title;
          return;
        }
        if (el.tagName !== 'SUMMARY') return;
        const details = el.parentElement, li = details.parentElement;
        const children = details.querySelector('.menu-drawer__submenu > .menu-drawer__menu');
        if (!children || li.tagName !== 'LI') return;
        // Build off-DOM, after the native drawer has bound its close controls.
        // Reuse the desktop disclosure component; do not repurpose a close button.
        const branch = document.createElement('eos-nav-branch'); branch.className = 'eos-nav-branch eos-mobile-branch';
        const row = document.createElement('div'); row.className = 'eos-nav-row';
        const link = document.createElement('a'); link.id = entry.id; link.href = url.pathname + url.search; link.textContent = entry.title;
        link.className = 'eos-mobile-parent menu-drawer__menu-item link link--text list-menu__item focus-inset';
        if (details.querySelector('[aria-current="page"]')) link.classList.add('menu-drawer__menu-item--active');
        const button = document.createElement('button'); button.type = 'button'; button.dataset.eosNavToggle = ''; button.hidden = true;
        const childId = 'EosMobileChildren-' + entry.id;
        button.setAttribute('aria-controls',childId); button.setAttribute('aria-expanded','true'); button.setAttribute('aria-label','Show subcollections for ' + entry.title);
        const icon = document.createElement('span'); icon.className = 'eos-mobile-expand-icon'; icon.setAttribute('aria-hidden','true'); button.append(icon);
        const list = children.cloneNode(true); list.id = childId; list.removeAttribute('tabindex');
        list.classList.add('eos-nav-children','eos-mobile-subcategories'); list.dataset.eosNavChildren = ''; list.dataset.eosMobileChildren = '';
        list.querySelectorAll('a').forEach(a => a.classList.add('eos-mobile-child'));
        row.append(link,button); branch.append(row,list); li.replaceChildren(branch);
      });
    });
    document.querySelectorAll('header-drawer').forEach(fitDrawer);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',setupMobile,{once:true}); else setupMobile();
  document.addEventListener('shopify:section:load',setupMobile);
  document.addEventListener('shopify:section:unload',event => {
    for (const [drawer,state] of measuredDrawers) if (event.target.contains(drawer)) { state.dispose(); measuredDrawers.delete(drawer); }
  });
  document.addEventListener('click',event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !(event.target instanceof Element)) return;
    const link = event.target.closest('header a[href]'); if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
    let url; try { url = sorted(link.href); } catch (_) { return; } if (!url) return;
    const home = document.querySelector('eos-everyday-home:not([data-eos-collection-page])');
    if (!home?.querySelector('[data-eos-guide]') || typeof home.loadCollection !== 'function') { link.href = url.pathname + url.search; return; }
    event.preventDefault();
    const drawer = link.closest('header-drawer');
    if (drawer && typeof drawer.closeMenuDrawer === 'function') { drawer.closeMenuDrawer(event,drawer.querySelector('summary')); drawer.querySelector('summary')?.setAttribute('aria-expanded','false'); }
    const menu = link.closest('header-menu'); if (menu) { if (typeof menu.close === 'function') menu.close(); else { menu.querySelector('details')?.removeAttribute('open'); menu.querySelector('summary')?.setAttribute('aria-expanded','false'); } }
    home.collectionTrigger = null; home.loadCollection(url.href);
  });
})();
